import { useState } from "react";
import { OCCURRENCE_CHAT_BODY_MAX } from "@edu/shared";
import { ApiError } from "../../lib/api.ts";
import { Loading, LoadFailed } from "../../lib/loading.tsx";
import { ChatFeed } from "../occurrence-chat/ChatFeed.tsx";
import {
  CHAT_CLOSED_TEXT,
  CHAT_FAILED_TEXT,
  CHAT_LABEL,
  chatErrorText,
} from "../occurrence-chat/chat-text.ts";
import type { OccurrenceChat } from "../occurrence-chat/use-occurrence-chat.ts";
import { ENTRY_CHAT_TITLE, canRemoveChatMessage, entryChatView } from "./entry-chat.ts";

/**
 * Чат занятия в карточке. Мест показа два, компонент один.
 *
 * Раздел 12 проекта (`docs/room-chat-design.md`) называет оба: всплывающая
 * карточка в сетке расписания и строка списка уроков. Двумя компонентами они
 * разъехались бы содержимым в первую же правку, поэтому здесь ровно один, а
 * различается только то, куда его вставили.
 *
 * ⚠️ **Высота блока задана ЯВНОЙ величиной, а не процентом или содержимым, и
 * это чинит сразу две вещи.** Первая названа в проекте прямо: у обоих
 * контейнеров высота от содержимого, и процент разрешился бы в `auto`, то есть
 * лента росла бы без потолка. Вторая тише и хуже: у всплывающей карточки висит
 * свой наблюдатель размера, который переставляет её на место при каждом
 * изменении высоты, и лента, выросшая ПОСЛЕ загрузки, дёргала бы карточку
 * из-под курсора. При фиксированной высоте загрузка, пустое состояние, лента и
 * форма занимают одну и ту же коробку, наблюдатель не просыпается вовсе, и
 * трогать его не пришлось.
 *
 * ⚠️ **Черновик живёт ВЫШЕ этого компонента**, в `useOccurrenceChat` у того, кто
 * держит карточку открытой. Всплывающая карточка закрывается на любое нажатие
 * мимо и на Escape, то есть набранная фраза уносилась бы движением мыши по
 * соседней клетке. Требование раздела 12 проекта дословно.
 *
 * ⚠️ **Поле в ОДНУ строку**, как и в комнате: растущая многострочка съедала бы
 * ленту, а на телефоне карточка приезжает нижним листом, где высоты и так мало.
 * Клавиатуру на узком экране разводит сама карточка (`EntryPopover`), поднимая
 * лист над ней по `visualViewport`.
 */
export function EntryChat(props: {
  chat: OccurrenceChat;
  /** Сторона ЧИТАТЕЛЯ. От неё зависят слова пустой ленты и право убрать чужое. */
  role: "teacher" | "student";
  /** Чат звонка с преподавателями: свои слова пустой ленты (`entry-chat.ts`). */
  call?: boolean;
}) {
  const { chat } = props;
  const view = entryChatView({ role: props.role, state: chat.state, call: props.call === true });
  // Одно «сейчас» на всю отрисовку: времена соседних сообщений обязаны
  // считаться от одного момента, иначе на границе полуночи они разойдутся
  const now = Date.now();

  /* Чата у занятия может не быть вовсе: у группы с погашенным рычагом либо со
     скрытым составом. Пустой блок обещал бы канал, которого нет */
  if (!view.shown) return null;

  return (
    <section className="echat" aria-label={CHAT_LABEL}>
      {/*
        Заголовок это «Чат», а не «Чат занятия»: слово «занятия» внутри карточки
        занятия указывает на то, что и так рядом. Полное имя при этом остаётся
        подписью области, потому что читалка, прыгающая по областям, соседней
        карточки не видит
      */}
      <p className="echat__title">{ENTRY_CHAT_TITLE}</p>

      {view.body === "loading" && <Loading />}
      {view.body === "failed" && <LoadFailed text={CHAT_FAILED_TEXT} onRetry={chat.retry} />}

      {(view.body === "empty" || view.body === "list") && (
        <>
          <ChatFeed
            chat={chat}
            className="echat__feed"
            emptyText={view.emptyText}
            now={now}
            /* ⚠️ Здесь удаление ЕСТЬ, в отличие от комнаты: карточку открывают,
               чтобы прочитать переписку задним числом, и снять свои слова с глаз
               остальных человек может только отсюда */
            removable={(m) => canRemoveChatMessage(props.role, m.mine)}
          />

          {view.canWrite ? (
            <EntryChatForm
              note={view.formNote}
              draft={chat.draft}
              onDraft={chat.setDraft}
              onSend={chat.send}
            />
          ) : (
            <p className="muted echat__shut">{CHAT_CLOSED_TEXT}</p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Написать в чат из карточки.
 *
 * ⚠️ **Своя форма, а не форма комнаты, и это решение.** Общего у них ровно
 * поведение (обрезать пробелы, не пускать пустое, показать отказ словами), и
 * оно уже общее: слова собирает `chatErrorText`, черновик держит хук. Разметка
 * же у них разная по существу: в комнате поле защищается от ПРОБЕЛА, потому что
 * при рисующем инструменте Excalidraw он перерисовывает холст на каждое
 * нажатие, а здесь холста нет вовсе, и тащить сюда чужую защиту значило бы
 * объяснять читателю ловушку, которой на этом экране не существует.
 */
function EntryChatForm(props: {
  note: string | null;
  draft: string;
  onDraft: (value: string) => void;
  onSend: (body: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const text = props.draft.trim();
  const send = (e: React.FormEvent) => {
    e.preventDefault();
    if (text === "" || busy) return;
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        await props.onSend(text);
      } catch (e2) {
        setErr(
          e2 instanceof ApiError
            ? chatErrorText(e2.status, e2.code, e2.message)
            : e2 instanceof Error
              ? e2.message
              : String(e2),
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <form className="echat__form" onSubmit={send}>
      {/*
        Подпись поля видима, а не спрятана: раздел 13 проекта называет её словом
        «Сообщение», и поле без подписи там, где рядом лента чужих слов,
        читается хуже, чем кажется автору
      */}
      <label className="echat__field">
        <span className="echat__label">Сообщение</span>
        <input
          className="fld"
          type="text"
          value={props.draft}
          maxLength={OCCURRENCE_CHAT_BODY_MAX}
          disabled={busy}
          onChange={(e) => props.onDraft(e.target.value)}
        />
      </label>
      {/* Постоянная подпись у группы: пустое состояние исчезает после первого
          сообщения, а единственное предупреждение исчезать не должно */}
      {props.note !== null && <p className="echat__who">{props.note}</p>}
      {err !== null && <p className="err">{err}</p>}
      <button type="submit" className="btn btn--quiet" disabled={busy || text === ""}>
        {busy ? "Отправляем…" : "Отправить"}
      </button>
    </form>
  );
}
